import { Button } from "@studyplus/boron-ui";
import { Chart } from "chart.js";
import classNames from "classnames";
import format from "date-fns/format";
import { useFormik } from "formik";
import flatMap from "lodash-es/flatMap";
import * as React from "react";
import { useState } from "react";
import * as yup from "yup";
import Icon from "../../../components/atoms/Icon";
import Label from "../../../components/atoms/Label";
import { Modal } from "../../../components/atoms/Modal";
import Textarea from "../../../components/atoms/Textarea";
import StudentAnalyticsGraph, {
  Props as GraphProps,
} from "../../../components/molecules/StudentAnalyticsGraph";
import { LineOutgoingMessageMaxLength } from "../../../consts/LineOutgoingMessageMaxLength";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { AnalyticsTermType } from "../../../interfaces/AnalyticsTableInterface";
import { StudentAnalyticsRowInterface } from "../../../interfaces/StudentAnalyticsTableInterface";
import StudentInterface from "../../../interfaces/StudentInterface";
import SharingAnalyticsGraph from "./SharingAnalyticsGraph";
import styles from "./styles.scss";
import { usePostAnalyticsShare } from "./usePostAnalyticsShare";

interface Props extends Partial<GraphProps> {
  startDate: Date | null;
  endDate: Date | null;
  term: AnalyticsTermType;
  student: StudentInterface;
  totalScore: number;
}

type Values = {
  message: string;
  image: Blob | null;
};

const canvasToBlob = (canvas: HTMLCanvasElement): Blob => {
  const base64 = canvas.toDataURL("image/png");
  // Base64からバイナリへ変換
  const bin = atob(base64.replace(/^.*,/, ""));
  const buffer = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    buffer[i] = bin.charCodeAt(i);
  }
  // Blobを作成
  const blob = new Blob([buffer.buffer], {
    type: "image/png",
  });
  return blob;
};

const formatDateRange = (
  startDate: Date | null,
  endDate: Date | null,
  term: AnalyticsTermType,
): string => {
  return `${formatDate(startDate, term)}〜${formatDate(endDate, term)}`;
};

const formatDate = (
  date: Date | null,
  term: AnalyticsTermType | undefined,
): string => {
  if (!date) {
    return "";
  }
  switch (term) {
    case "weekly":
      return format(date, "yyyy年MM月dd日週");
    case "monthly":
      return format(date, "yyyy年MM月");
    case "daily":
    default:
      return format(date, "yyyy年MM月dd日（E）");
  }
};

const AnalyticsShareButton: React.FC<Props> = ({
  student,
  learningMaterials,
  analyticsType,
  columns,
  startDate,
  endDate,
  term,
  totalScore,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [chart, setChart] = useState<Chart | null>(null);

  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const { mutate, isPending } = usePostAnalyticsShare();

  const sumScore = flatMap(
    learningMaterials,
    (learningMateral: StudentAnalyticsRowInterface) => learningMateral.scores,
  ).reduce((prev: number, current = 0) => prev + current, 0);

  const setSendImage = (canvas: HTMLCanvasElement) => {
    const image = canvasToBlob(canvas);
    formik.setFieldValue("image", image);
  };

  const rangeString = formatDateRange(startDate, endDate, term);
  const analyticsTypeLabel = analyticsType === "amount" ? "学習量" : "学習時間";
  const placeholder = `${student.fullName}さんの${rangeString}の${analyticsTypeLabel}をお送りします。`;

  const formik = useFormik<Values>({
    initialValues: { message: "", image: null },
    validationSchema,
    onSubmit: (values) => {
      const sendMessage =
        Array.from(values.message).length > 0 ? values.message : placeholder;
      if (!values.image) {
        return;
      }
      mutate(
        {
          studentId: student.id,
          message: sendMessage,
          image: values.image,
        },
        {
          onSuccess: () => {
            setIsOpen(false);
            showSuccessMessage("アナリティクスを送信しました");
          },
          onError: () => showErrorMessage("アナリティクスの送信に失敗しました"),
        },
      );
    },
  });

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const messageLength = Array.from(formik.values.message || "").length;

  if (student.lineConnectionStatus !== "connected") {
    return null;
  }

  if (!learningMaterials || !analyticsType || !columns) {
    return (
      <button className={classNames([styles.button, styles.disabled])}>
        <Icon name="icon-line" className={styles.lineIcon} />
      </button>
    );
  }

  return (
    <>
      <button className={styles.button} onClick={handleOpen}>
        <Icon name="icon-line" className={styles.lineIcon} />
      </button>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className={styles.modal}
      >
        <Modal.Header onClose={handleClose}>
          <h2>{student.fullName}のアナリティクスを送信</h2>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formik.handleSubmit}>
            <div className={styles.messageLabelLine}>
              <Label className={styles.messageLabel}>メッセージ</Label>
              <div className={styles.messageLength}>
                {messageLength}/{LineOutgoingMessageMaxLength}字
              </div>
            </div>
            <Textarea
              name="message"
              value={formik.values.message}
              onChange={formik.handleChange}
              placeholder={placeholder}
              className={styles.message}
              hasError={!!formik.errors.message}
            />
            <SharingAnalyticsGraph
              startDate={startDate}
              endDate={endDate}
              term={term}
              rangeString={rangeString}
              chart={chart}
              sumScore={sumScore}
              totalScore={totalScore}
              analyticsType={analyticsType}
              setSendImage={setSendImage}
            />
            <div className={styles.submitButton}>
              <Button disabled={isPending || !formik.isValid} type="submit">
                送信
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <div className={styles.render}>
        <StudentAnalyticsGraph
          learningMaterials={learningMaterials}
          term={term}
          analyticsType={analyticsType}
          columns={columns}
          setChart={setChart}
        />
      </div>
    </>
  );
};

const validationSchema = yup.object().shape({
  message: yup.string().trim().min(0).max(LineOutgoingMessageMaxLength),
  image: yup.mixed().required(),
});

export default AnalyticsShareButton;
